import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { maskPhone } from "util/maskPhone";
import Dialog from "modules/appointments/components/Dialog";
import CreateAppointmentService from "modules/appointments/service/CreateAppointmentService";
import formatCurrency from "modules/appointments/helper/formatCurrency";
import WalletRulesInfoBox from "./WalletRulesInfoBox";
import doneGif from "assets/images/done-animation.gif";
import errorImg from "assets/icons/payment/error_red_exclamation.svg";
import {
  SCREEN_STATE,
  PAYMENT_METHOD,
  NO_VALID_PAYMENT_METHOD,
  validPaymentOptions,
  isOptionTooSmall,
} from "modules/appointments/helper/paymentLinkConstants";
import "../../styles/paymentLink.scss";

/**
 * Create-with-payment. Ported from bridge's PaymentLinkDialog, create branch
 * only (`withCreateAppointment` is always true here), so bridge's
 * existing-appointment path — which has a missing-return bug and can fall
 * through to `undefined` options — is not carried over.
 *
 * Four bridge bugs deliberately NOT ported (each noted at its site):
 *  1. "Link copied" emitted on the ERROR channel.
 *  2. "…and payment link generated successfully" fired for FREE appointments,
 *     where no link exists.
 *  3. "Try generating link again" that retries nothing.
 *  4. No terminal exit on the success/failure screens.
 *
 * Flow: price 0 -> create raw -> DONE. price > 0 -> sublimit -> method ->
 * share options -> create with paymentLinkRequest -> link.
 */
const DECIDING = -1;

const PaymentLinkDialog = ({ payload, price, patient, onClose, onDone }) => {
  const service = useRef(new CreateAppointmentService()).current;
  const [screen, setScreen] = useState(DECIDING);
  const [busy, setBusy] = useState(true);
  const [sublimit, setSublimit] = useState(null);
  const [method, setMethod] = useState(null);
  const [created, setCreated] = useState(null);
  const [linkBody, setLinkBody] = useState(null);
  const [errText, setErrText] = useState("");
  /** The share options chosen on state 1, so a retry can reuse them. */
  const shareRef = useRef(null);
  /* The share screen's in-progress edits, held here so Back -> Continue does not
     silently revert a corrected number to the profile's. Owned by the dialog
     because the screen itself unmounts on Back. */
  const shareDraftRef = useRef(null);

  const showError = (message) => EventService.emit("showError", { message });
  /* NOT "showSuccess" — nothing subscribes to that event. ToastHost listens for
     "showError" only and renders green when `tone` says so
     (ToastHost.js:25, :39), which is how every other success message in phr is
     sent (HealthPlan.js:548, CallNoteModal.js:211). Emitting "showSuccess" here
     meant all five confirmations in this dialog — including "Link copied." —
     were silent. */
  const showSuccess = (message) =>
    EventService.emit("showError", { message, tone: "success" });

  const isFree = price === 0;

  const create = useCallback(
    (body, nextScreen) => {
      setBusy(true);
      service.createAppointment(
        body,
        (res) => {
          setBusy(false);
          setCreated(res);

          // Bridge fires "Appointment created and payment link generated
          // successfully" unconditionally — including for free appointments,
          // where no link was ever generated. Say what actually happened.
          if (isFree) {
            showSuccess("Appointment created.");
            setScreen(SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET);
            return;
          }

          const link = res && res.paymentDetails && res.paymentDetails.paymentLink;
          if (nextScreen === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && !link) {
            showSuccess("Appointment created, but the payment link wasn't generated.");
            setScreen(SCREEN_STATE.APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED);
            return;
          }
          showSuccess(
            nextScreen === SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET
              ? "Appointment created and paid from wallet."
              : "Appointment created and payment link generated."
          );
          setLinkBody(link);
          setScreen(nextScreen);
        },
        (err) => {
          setBusy(false);
          setErrText((err && err.message) || "");
          setScreen(SCREEN_STATE.ERROR_CREATING_APPOINTMENT);
        }
      );
    },
    [service, isFree]
  );

  // Entry: free books immediately; paid needs the wallet picture first.
  useEffect(() => {
    if (isFree) {
      // Raw payload — no paymentLinkRequest, no paymentDetails, no useWallet.
      // The server computes payable <= 0 and marks it SPONSORED.
      create(payload, SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET);
      return;
    }
    setBusy(true);
    service.checkCurrentWalletDetails(
      {
        patientID: payload.patientID,
        // Bridge sends the patient id here when bookedFor is 0, NOT 0.
        bookedFor: payload.bookedFor === 0 ? payload.patientID : payload.bookedFor,
        planID: payload.planID,
        serviceID: payload.serviceID,
        grandTotal: price,
      },
      (res) => {
        setBusy(false);
        setSublimit(res);
        setScreen(SCREEN_STATE.SELECT_PAYMENT_METHOD);
      },
      (err) => {
        setBusy(false);
        showError(`Couldn't load payment options: ${(err && err.message) || ""}`);
        onClose();
      }
    );
    // Runs once on mount by design — payload/price are fixed for this dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => validPaymentOptions(sublimit), [sublimit]);

  // Pick the first selectable option; -1 when nothing is.
  useEffect(() => {
    if (!options.length) return;
    if (method && options.indexOf(method) !== -1) return;
    const first = options.find((o) => !isOptionTooSmall(o, sublimit) && o !== PAYMENT_METHOD.WALLET_DISABLED);
    setMethod(first || NO_VALID_PAYMENT_METHOD);
  }, [options, sublimit, method]);

  const submitPaid = (paymentLink) => {
    shareRef.current = paymentLink;
    const useWallet = method !== PAYMENT_METHOD.RAZORPAY_ONLY;
    const paymentLinkRequest = {
      // Echoed verbatim. The server compares these against its own recomputed
      // sublimit and rejects with "payment details are changed during
      // apppointment creation" if they differ — so never reshape this object.
      paymentDetails: { ...sublimit },
      useWallet,
    };
    if (paymentLink) paymentLinkRequest.paymentLink = paymentLink;

    create(
      { ...payload, paymentLinkRequest },
      paymentLink
        ? SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK
        : SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET
    );
  };

  const retryLink = () => {
    if (!created || !created.id) return;
    setBusy(true);
    service.generatePaymentLink(
      {
        appointmentId: created.id,
        useWallet: method !== PAYMENT_METHOD.RAZORPAY_ONLY,
        // The share options the doctor already chose. Dropping them means the
        // server has no channel to send on and rejects the retry — and the
        // patient would never receive anything.
        ...(shareRef.current ? { paymentLink: shareRef.current } : {}),
      },
      (res) => {
        setBusy(false);
        const link = res && res.paymentLink;
        if (!link) {
          // Same guard as create(): never claim a link was shared when the
          // response carries none.
          showError("The link still couldn't be generated.");
          return;
        }
        setLinkBody(link);
        setScreen(SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK);
        showSuccess("Payment link generated.");
      },
      (err) => {
        setBusy(false);
        // Honest: bridge's retry button navigates away and retries nothing.
        showError(`Couldn't generate the link: ${(err && err.message) || ""}`);
      }
    );
  };

  /* Bridge titles both pre-create screens "Proceed to pay" and keeps the grand
     total under it on both, so the operator never commits to sending a link
     with the amount off-screen. phr showed the amount on the method screen
     only. */
  const title =
    screen === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS ||
    screen === SCREEN_STATE.SELECT_PAYMENT_METHOD
      ? "Proceed to pay"
      : "Create appointment";

  // No backdrop/Escape dismissal on a money dialog, and none at all while a
  // create is in flight — but every terminal screen gets an explicit exit.
  const dismissible = !busy && (screen === SCREEN_STATE.SELECT_PAYMENT_METHOD || screen === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS);

  return (
    <Dialog title={title} onClose={onClose} dismissible={dismissible} width="520px">
      {busy && <p className="pl-busy">Working…</p>}

      {!busy && screen === SCREEN_STATE.SELECT_PAYMENT_METHOD && (
        <SelectPaymentMethod
          sublimit={sublimit}
          options={options}
          method={method}
          setMethod={setMethod}
          price={price}
          onCancel={onClose}
          onNext={() => {
            if (method === PAYMENT_METHOD.WALLET_ONLY) {
              // Fully covered by wallet: no link to share, so skip step 1.
              submitPaid(null);
              return;
            }
            setScreen(SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS);
          }}
        />
      )}

      {!busy && screen === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS && (
        <>
          <p className="pl-total">
            Grand total <strong>{formatCurrency(price)}</strong>
          </p>
          <SelectShareLinkOptions
            patient={patient}
            draftRef={shareDraftRef}
            onBack={() => setScreen(SCREEN_STATE.SELECT_PAYMENT_METHOD)}
            onSubmit={submitPaid}
          />
        </>
      )}

      {!busy && screen === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && (
        <SuccessWithLink
          link={linkBody}
          created={created}
          onDone={() => onDone(created && created.id)}
          showSuccess={showSuccess}
        />
      )}

      {!busy && screen === SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET && (
        /* Bridge's SuccessTotalPaymentWithWallet, verbatim: the mark, the one
           title, the button. It says nothing about HOW it was paid — and phr's
           extra line did, which is a difference the operator has to reconcile
           against a screen that does not say it. */
        <div className="pl-done">
          <img className="pl-done-mark" src={doneGif} alt="" />
          <h3 className="pl-done-title">Appointment created successfully!</h3>
          <div className="pl-actions is-center">
            <button type="button" className="zy-btn zy-btn-primary" onClick={() => onDone(created && created.id)}>
              Done
            </button>
          </div>
        </div>
      )}

      {!busy && screen === SCREEN_STATE.ERROR_CREATING_APPOINTMENT && (
        /* Bridge's ErrorInCreatingAppointment: mark, the banner line, then the
           server's own text, then "Okay". */
        <div className="pl-done">
          <img className="pl-done-mark is-error" src={errorImg} alt="" />
          <div className="pl-note-box is-error">
            <strong>Error in creating appointment</strong>
          </div>
          <p className="pl-done-sub">{errText || "Something went wrong."}</p>
          <div className="pl-actions is-center">
            <button type="button" className="zy-btn zy-btn-primary" onClick={onClose}>
              Okay
            </button>
          </div>
        </div>
      )}

      {!busy && screen === SCREEN_STATE.APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED && (
        /* Bridge's AptCreatedButLinkNotGenerated: the success mark and title —
           the appointment DID get created — then a bordered block carrying the
           error mark, the message and the retry. */
        <div className="pl-done">
          <img className="pl-done-mark" src={doneGif} alt="" />
          <h3 className="pl-done-title">Appointment created successfully!</h3>
          <div className="pl-dashed">
            <img className="pl-done-mark is-small" src={errorImg} alt="" />
            <div className="pl-note-box is-error">
              <strong>Error in generating payment link</strong>
            </div>
            <div className="pl-actions is-center">
              {/* A real retry: bridge's button only navigates to its detail
                  view, which retries nothing. */}
              <button type="button" className="zy-btn zy-btn-primary" onClick={retryLink}>
                Try generating link again
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

/**
 * The info affordance beside a wallet amount. A button, not an icon-only span:
 * it toggles a panel, so it has to be reachable and announceable.
 */
const RulesToggle = ({ open, onToggle }) => (
  <button
    type="button"
    className="pl-rules-toggle"
    aria-expanded={open}
    aria-label={open ? "Hide wallet rules" : "Why this amount?"}
    title="Why this amount?"
    onClick={(e) => {
      /* Inside a <label>, so a bare click would also toggle the radio. */
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }}
  >
    i
  </button>
);
RulesToggle.propTypes = { open: PropTypes.bool, onToggle: PropTypes.func };

/* ---------- state 0 ---------- */
const METHOD_LABEL = {
  [PAYMENT_METHOD.WALLET_AND_RAZORPAY]: "Wallet + payment link",
  [PAYMENT_METHOD.RAZORPAY_ONLY]: "Payment link only",
  [PAYMENT_METHOD.WALLET_ONLY]: "Wallet only",
  [PAYMENT_METHOD.WALLET_DISABLED]: "Wallet (empty)",
};

const SelectPaymentMethod = ({ sublimit, options, method, setMethod, price, onNext, onCancel }) => {
  const zyla = sublimit && sublimit.useZylaWallet;
  const ext = sublimit && sublimit.useExternalWallet;
  const nothingValid = method === NO_VALID_PAYMENT_METHOD || !options.length;
  const [showRules, setShowRules] = useState(false);

  return (
    <div className="pl-methods">
      <p className="pl-total">
        Grand total <strong>{formatCurrency(price)}</strong>
      </p>

      {options.map((o) => {
        const off = o === PAYMENT_METHOD.WALLET_DISABLED || isOptionTooSmall(o, sublimit);
        return (
          <label key={o} className={`pl-radio${off ? " is-disabled" : ""}`}>
            <input
              type="radio"
              name="pl-method"
              value={o}
              disabled={off}
              checked={method === o}
              onChange={() => setMethod(o)}
            />
            <span className="pl-radio-body">
              <span className="pl-radio-label">{METHOD_LABEL[o]}</span>
              {o === PAYMENT_METHOD.WALLET_AND_RAZORPAY && (
                <span className="pl-radio-hint">
                  {formatCurrency(zyla)} from wallet, {formatCurrency(ext)} via link
                  <RulesToggle open={showRules} onToggle={() => setShowRules((v) => !v)} />
                </span>
              )}
              {o === PAYMENT_METHOD.WALLET_ONLY && (
                <span className="pl-radio-hint">
                  {formatCurrency(zyla)} from wallet
                  <RulesToggle open={showRules} onToggle={() => setShowRules((v) => !v)} />
                </span>
              )}
              {showRules &&
                (o === PAYMENT_METHOD.WALLET_AND_RAZORPAY || o === PAYMENT_METHOD.WALLET_ONLY) && (
                  <WalletRulesInfoBox sublimit={sublimit} />
                )}
              {o === PAYMENT_METHOD.WALLET_DISABLED && (
                <span className="pl-radio-hint">No balance available</span>
              )}
              {isOptionTooSmall(o, sublimit) && (
                <span className="pl-radio-hint">
                  Amount is under Rs 1 — it can&rsquo;t be charged online.
                </span>
              )}
            </span>
          </label>
        );
      })}

      {nothingValid && (
        <p className="pl-error">No payment method can be used for this amount.</p>
      )}

      {/* ALWAYS shown, and it changes with the selection. The WALLET_ONLY
          variant is the one that matters: that method debits the wallet the
          moment Continue is pressed, with no link and no second confirmation,
          and phr said nothing about it. The other two variants are bridge's
          too — the "under Rs 1" explanation for a method that cannot be
          charged, and otherwise the warning that the choice is final. */}
      <div className="pl-note-box">
        <strong>Note:</strong>{" "}
        {isOptionTooSmall(method, sublimit) ? (
          <>
            You can&rsquo;t proceed with this payment method — a payment of less
            than {formatCurrency(1)} can&rsquo;t be charged online.
          </>
        ) : method === PAYMENT_METHOD.WALLET_ONLY ? (
          <>Once you click Continue, the amount is deducted from the wallet immediately.</>
        ) : (
          <>The selected payment preferences can&rsquo;t be changed after Continue.</>
        )}
      </div>

      <div className="pl-actions">
        <button type="button" className="zy-btn zy-btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="zy-btn zy-btn-primary" disabled={nothingValid} onClick={onNext}>
          Continue
        </button>
      </div>
    </div>
  );
};

/* ---------- state 1 ---------- */
/**
 * Where the payment link goes. Content and ORDER are bridge's
 * SelectShareLinkOptions, because this is the last screen before the
 * appointment is created and the link is dispatched — an operator who cannot
 * see the country code, the recipient of the chat message, or the fact that
 * the choice is final is being asked to commit blind.
 *
 * Bridge lays the four rows out in a 12-column grid inside a 720px dialog;
 * phr's dialog is a 520px stack, so each row is caption-then-field rather than
 * side by side. That is layout, not content: every label, caption, field and
 * note bridge shows is shown here, in bridge's order.
 *
 * TWO DELIBERATE DEVIATIONS, both because bridge's version loses data:
 *  1. The phone guard. Bridge tests the whole candidate value
 *     (`/^\d{0,10}$/.test(value)`) and DROPS it when it fails, so pasting
 *     "+919876543210" silently leaves the previously-seeded number in the
 *     field — the operator believes they corrected it and the link goes to the
 *     old number. phr strips then caps, which is what phr's own create form
 *     already does (CreateAppointment.js `.replace(/[^0-9]/g, "").slice(0, 10)`).
 *  2. The Back button, which bridge does not have. Keeping it means the edits
 *     made here have to survive the round trip, so they are held in a ref owned
 *     by the dialog — otherwise Back silently reverts a corrected number to the
 *     profile's, which is the same wrong-handset failure as (1).
 */
const SelectShareLinkOptions = ({ patient, draftRef, onBack, onSubmit }) => {
  const cc = (patient && patient.countryCode) || 91;
  const phone = (patient && patient.phoneno) || "";
  const mail = (patient && patient.email) || "";
  const patientId = patient && patient.id ? String(patient.id) : "";

  /* Seeded from the patient record on first entry, from the draft on a return
     trip through Back. */
  const seed = (draftRef && draftRef.current) || null;

  const [sms] = useState(true); // forced, see below
  const [smsNo, setSmsNo] = useState(seed ? seed.smsNo : String(phone));
  const [whatsapp, setWhatsapp] = useState(seed ? seed.whatsapp : !!phone);
  const [waNo, setWaNo] = useState(seed ? seed.waNo : String(phone));
  const [email, setEmail] = useState(seed ? seed.email : !!mail);
  const [addr, setAddr] = useState(seed ? seed.addr : mail);
  /* ON by default, matching bridge. Chat is the one channel that reaches the
     app the patient already has open, so defaulting it off quietly dropped a
     delivery route on every booking. */
  const [chat, setChat] = useState(seed ? seed.chat : true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (draftRef) draftRef.current = { smsNo, whatsapp, waNo, email, addr, chat };
  }, [draftRef, smsNo, whatsapp, waNo, email, addr, chat]);

  /* Bridge's isAnyOptionSelected. Unreachable today — SMS is pinned true in
     both repos — but bridge guards it, and the day SMS becomes tickable a
     missing guard here submits an all-false body the server cannot deliver. */
  const anySelected = sms || whatsapp || email || chat;

  /* Strip, then cap. Never reject the whole value: see deviation (1) above. */
  const digits = (v) => String(v || "").replace(/[^0-9]/g, "").slice(0, 10);
  /* A corrected field must not keep showing the error it was corrected for. */
  const edit = (setter, mapper) => (e) => {
    setErr("");
    setter(mapper ? mapper(e.target.value) : e.target.value);
  };

  const go = () => {
    if (!anySelected) return;
    // 10 digits, matching bridge. Emptiness alone is not enough: the server's
    // only guard is a 1e5..1e15 range, so a mistyped 6-digit number passes
    // validation and the payment link is sent to nobody.
    const tenDigits = (v) => /^\d{10}$/.test(String(v || ""));
    if (!tenDigits(smsNo)) return setErr("Enter a 10-digit SMS number.");
    if (whatsapp && !tenDigits(waNo)) return setErr("Enter a 10-digit WhatsApp number.");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((addr || "").trim())) {
      return setErr("Enter a valid email address.");
    }
    onSubmit({
      sendWhatsapp: whatsapp,
      whatsappNo: whatsapp ? { countryCode: cc, phoneNo: Number(waNo) } : null,
      sendSms: sms,
      smsNumber: sms ? { countryCode: cc, phoneNo: Number(smsNo) } : null,
      sendEmail: email,
      emailAddress: email ? addr : null,
      sendChat: chat,
    });
  };

  return (
    <div className="pl-share">
      <p className="pl-share-title">Would you like to send payment link to user?</p>

      {/* SMS */}
      <div className="pl-share-row">
        <label className="pl-check is-locked" htmlFor="pl-opt-sms">
          <input id="pl-opt-sms" type="checkbox" checked={sms} readOnly disabled />
          <span>Send to SMS</span>
        </label>
        {/* Not cosmetic: the server rejects sendSms:false outright — "SMS is
            mandatory for payment link communication channel"
            (validations/appointment_validations.go:212, still current on
            development). Bridge hard-disables the same checkbox — it does not
            offer the choice either — but it never says why, so an operator
            there is left guessing. */}
        <p className="pl-note">SMS is always sent — the server requires it.</p>
        <div className="pl-field">
          <label className="pl-field-label" htmlFor="pl-sms-no">
            Patient number
          </label>
          <div className="pl-phone">
            {/* Shown, not editable — exactly as bridge shows it. phr already
                SENT this code and never displayed it, so a number being
                corrected here was being edited without its prefix in view. */}
            <input
              className="cf-input pl-cc"
              type="text"
              value={`+${cc}`}
              disabled
              aria-label="SMS country code"
            />
            <input
              id="pl-sms-no"
              className="cf-input"
              type="text"
              inputMode="numeric"
              value={smsNo}
              onChange={edit(setSmsNo, digits)}
            />
          </div>
        </div>
      </div>

      {/* WHATSAPP */}
      <div className="pl-share-row">
        <label className="pl-check" htmlFor="pl-opt-whatsapp">
          <input
            id="pl-opt-whatsapp"
            type="checkbox"
            checked={whatsapp}
            onChange={() => {
              setErr("");
              setWhatsapp((v) => !v);
            }}
          />
          <span>Send to WhatsApp</span>
        </label>
        <div className="pl-field">
          <label className="pl-field-label" htmlFor="pl-wa-no">
            Patient number
          </label>
          <div className="pl-phone">
            <input
              className="cf-input pl-cc"
              type="text"
              value={`+${cc}`}
              disabled
              aria-label="WhatsApp country code"
            />
            {/* Disabled rather than unmounted, as bridge does it: the number
                that WOULD be used stays readable before the box is ticked, and
                the row keeps its height instead of the whole screen reflowing
                on every toggle. */}
            <input
              id="pl-wa-no"
              className="cf-input"
              type="text"
              inputMode="numeric"
              disabled={!whatsapp}
              value={waNo}
              onChange={edit(setWaNo, digits)}
            />
          </div>
        </div>
      </div>

      {/* EMAIL */}
      <div className="pl-share-row">
        <label className="pl-check" htmlFor="pl-opt-email">
          <input
            id="pl-opt-email"
            type="checkbox"
            checked={email}
            onChange={() => {
              setErr("");
              setEmail((v) => !v);
            }}
          />
          <span>Send to Email</span>
        </label>
        <div className="pl-field">
          <label className="pl-field-label" htmlFor="pl-email-addr">
            E-mail
          </label>
          <input
            id="pl-email-addr"
            className="cf-input"
            type="text"
            placeholder="Type here"
            maxLength={50}
            disabled={!email}
            value={addr}
            onChange={edit(setAddr)}
          />
        </div>
      </div>

      {/* ZYLA CHAT */}
      <div className="pl-share-row">
        <label className="pl-check" htmlFor="pl-opt-chat">
          <input
            id="pl-opt-chat"
            type="checkbox"
            checked={chat}
            onChange={() => {
              setErr("");
              setChat((v) => !v);
            }}
          />
          <span>Send to Zyla chat</span>
        </label>
        <div className="pl-field">
          {/* Chat is ticked by default, so without this it was the one channel
              armed with no visible recipient anywhere on the screen. */}
          <label className="pl-field-label" htmlFor="pl-chat-pid">
            Patient ID
          </label>
          <input
            id="pl-chat-pid"
            className="cf-input"
            type="text"
            value={patientId}
            disabled
          />
        </div>
      </div>

      {err && <p className="pl-error">{err}</p>}

      {/* Bridge's finality warning. phr carried an equivalent on the method
          screen only, so the screen where the channels are actually chosen said
          nothing about the choice being final. */}
      <div className="pl-note-box">
        <strong>Note:</strong> The selected payment preferences cannot be modified
        after clicking <strong>&lsquo;Create appointment &amp; share payment link&rsquo;</strong>
      </div>

      <div className="pl-actions">
        <button type="button" className="zy-btn zy-btn-ghost" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="zy-btn zy-btn-primary"
          disabled={!anySelected}
          onClick={go}
        >
          Create appointment &amp; share payment link
        </button>
      </div>
    </div>
  );
};
/* ---------- state 2 ---------- */
/** "+91 XXXXX X5025" from the wire's { countryCode, phoneNo } — masked, as
    every phone number shown is (util/maskPhone). */
const phoneOf = (n) => (n ? maskPhone(n.countryCode, n.phoneNo) : "");

/** Bridge's copy control is Material `content_copy`; phr loads no icon font. */
const IconCopy = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);

/**
 * State 2 — the appointment exists and the link went out.
 *
 * Content and ORDER are bridge's SuccessWithGeneratedLink, deliberately: mark,
 * title, subtitle, the channels the link actually went to, a divider, the
 * cancellation note, the link box, Done. phr had the link ABOVE the note and
 * printed an appointment id where bridge lists the channels — so two operators
 * comparing the same booking across the two apps saw different facts in a
 * different order.
 *
 * THE FIELD IS `link` (models/appointment_payment.go:30); the earlier
 * shortUrl/url/paymentLink probes matched nothing on the wire.
 */
const SuccessWithLink = ({ link, created, onDone, showSuccess }) => {
  const url = (link && link.link) || "";
  /* Only the channels the server says it used — each row is conditional on its
     own flag, so an unused channel is absent rather than shown blank. */
  const rows = [
    link && link.sendWhatsapp && ["Sent to Whatsapp", phoneOf(link.whatsappNo)],
    link && link.sendEmail && ["Sent to Email", link.emailAddress || ""],
    link && link.sendSms && ["Sms sent", phoneOf(link.smsNumber)],
    link && link.sendChat && ["Sent to chat", String((created && created.patientID) || "")],
  ].filter(Boolean);

  return (
    <div className="pl-done">
      <img className="pl-done-mark" src={doneGif} alt="" />
      <h3 className="pl-done-title">Appointment created successfully!</h3>
      <p className="pl-done-sub">Payment link is successfully shared to user</p>

      {rows.length > 0 && (
        <div className="pl-sent">
          {rows.map(([label, value]) => (
            <div className="pl-sent-row" key={label}>
              <span className="pl-sent-label">&bull; {label}</span>
              <span className="pl-sent-value">{value}</span>
            </div>
          ))}
        </div>
      )}

      <span className="pl-divider" aria-hidden="true" />

      {/* Bridge's sentence, word for word — and its TIME-ONLY stamp, lowercased
          ("04:28 pm"). phr printed "09/09/2026, 18:11:29": the same instant,
          said differently on the two screens. */}
      <p className="pl-note is-block">
        <strong>Please note:</strong> This link will be valid for only{" "}
        {link && link.linkValidMins} mins, if payment is not made, the
        appointment will be cancelled at{" "}
        <strong>
          {link && link.expireAt
            ? new Date(link.expireAt)
                .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
                .toLowerCase()
            : ""}
        </strong>
        .
      </p>

      <div className="pl-linkbox">
        <span className="pl-linkbox-label">Payment link:</span>
        <span className="pl-linkbox-url">{url}</span>
        <button
          type="button"
          className={`pl-linkbox-copy${url ? "" : " is-off"}`}
          disabled={!url}
          aria-label="Copy payment link"
          onClick={() => {
            if (!url) return;
            if (navigator.clipboard) navigator.clipboard.writeText(url);
            showSuccess("Link copied");
          }}
        >
          <IconCopy />
        </button>
      </div>

      <div className="pl-actions is-center">
        <button
          type="button"
          className="zy-btn zy-btn-primary"
          onClick={() => onDone(created && created.id)}
        >
          Done
        </button>
      </div>
    </div>
  );
};

PaymentLinkDialog.propTypes = {
  payload: PropTypes.object.isRequired,
  price: PropTypes.number.isRequired,
  patient: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default PaymentLinkDialog;
